import React, {useMemo, useState, useEffect} from "react";
import HighchartsReact from "highcharts-react-official";
import { blue, red, green, orange, purple, teal, pink, indigo } from '@mui/material/colors'

import {
    getDayOfYearLabel,
    getSeason,
    isLeapYear,
    groupBy,
    createDayOfMonthLabels,
    trimLabels,
    extractSeason, getSeasonLabel, extractAvailableSeasons
} from "../utils";
import SeasonSelect from "../SeasonSelect";
import Stack from "@mui/material/Stack";
import MDBox from "../../../materialDashboard/components/MDBox";
import {useTranslation} from "react-i18next";
import Highcharts, {chartLogoOptions, creditsOptions, exportingOptions} from "../highChartsBase";
import useHighChartLocaleOptions from "../useHighChartLocaleOptions";

const colors = [blue[900], red[500], green[500], orange[500], purple[500], teal[500], pink[500], indigo[500]];

const HSComparisonGraph = ({stationsData, stations}) => {
    const {t} = useTranslation(["graphs", "webgis"]);
    const highChartLocaleOptions = useHighChartLocaleOptions();

    // Transform data for each station
    const transformedData = useMemo(() => {
        const result = {};
        Object.entries(stationsData).forEach(([stationId, data]) => {
            result[stationId] = (data || []).map(({timestamp, ...rest}) => {
                const date = new Date(timestamp);
                return {
                    timestamp: date,
                    label: getDayOfYearLabel(date),
                    season: getSeason(date),
                    ...rest
                }
            });
        });
        return result;
    }, [stationsData]);

    // Available seasons (from all stations data)
    const availableSeasons = useMemo(() => {
        const allSeasons = new Set();
        Object.values(transformedData).forEach(data => {
            extractAvailableSeasons(data).forEach(s => allSeasons.add(s));
        });
        return Array.from(allSeasons).sort((a, b) => b - a);
    }, [transformedData]);

    const [selectedSeason, setSelectedSeason] = useState(availableSeasons[0] ?? null);

    useEffect(() => {
        if (selectedSeason === null && availableSeasons.length > 0) {
            setSelectedSeason(availableSeasons[0]);
        }
    }, [availableSeasons, selectedSeason]);

    // Filter data for selected season
    const seasonData = useMemo(() => {
        const result = {};
        Object.entries(transformedData).forEach(([stationId, data]) => {
            result[stationId] = groupBy(extractSeason(data, selectedSeason), 'label');
        });
        return result;
    }, [transformedData, selectedSeason]);

    const withLeapYears = isLeapYear(selectedSeason + 1);
    const labels = createDayOfMonthLabels(withLeapYears);

    // Trim labels based on all data
    const trimmedLabels = useMemo(() => {
        const allDataPoints = {};
        Object.values(seasonData).forEach(data => {
            Object.assign(allDataPoints, data);
        });
        return trimLabels(labels, allDataPoints);
    }, [labels, seasonData]);

    const series = useMemo(() => {
        return stations.map((station, index) => {
            const stationId = station.id;
            const data = seasonData[stationId] || {};
            return {
                id: `hs-station-${stationId}`,
                data: trimmedLabels.map(label => data[label]?.[0]?.hs ?? null),
                type: 'line',
                name: `${station.code || station.name}`,
                color: colors[index % colors.length],
                yAxis: 0,
                marker: {
                    enabled: true,
                    radius: 3
                }
            };
        });
    }, [stations, seasonData, trimmedLabels]);

    const options = {
        ...highChartLocaleOptions,
        exporting: {
            ...exportingOptions.exporting,
            filename: `stations_comparison_hs`
        },
        title: {
            text: t('hsComparisonGraph.title', 'Stations Comparison - HS')
        },
        subtitle: {
            text: t('hsComparisonGraph.subtitle', {season: getSeasonLabel(selectedSeason)})
        },
        series: series,
        xAxis: {
            categories: trimmedLabels,
            gridLineWidth: 1
        },
        yAxis: [
            {
                title: {
                    text: 'HS [cm]',
                    gridLineWidth: 1
                }
            },
        ],
        tooltip: {
            shared: true,
            crosshairs: true,
            formatter: function () {
                let tooltip = '<b>' + this.x + '</b>';
                this.points.forEach(point => {
                    tooltip += '<br/><span style="color:' + point.series.color + '">●</span> ' + point.series.name + ': <b>' + Math.round(point.y) + '</b>';
                });
                return tooltip;
            }
        },
        plotOptions: {
            series: {
                marker: {
                    enabled: true,
                    symbol: 'circle',
                },
                line: {
                    connectNulls: false
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                }
            }
        },
        chart: {
            zoomType: 'x',
            height: 500,
            ...chartLogoOptions
        },
        ...creditsOptions,
    }

    return (
        <Stack>
            <MDBox sx={{py: 2}}>
                <SeasonSelect selectedSeason={selectedSeason}
                              setSelectedSeason={setSelectedSeason}
                              sx={{minWidth: "12rem"}}
                              availableSeasons={availableSeasons}
                />
            </MDBox>
            <HighchartsReact
                highcharts={Highcharts}
                options={options}
            />
        </Stack>
    );
}

export default HSComparisonGraph;
